import React, { useState, useEffect, useMemo } from 'react';
import styles from './StoreOrdering.module.css';

/**
 * ProductSearch - Search bar with paginated product results
 * Features a + button for adding new products
 */
const ProductSearch = ({
  products,
  onProductSelect,
  onAddNewClick,
  cartItems = []
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Filter products based on search
  const filteredProducts = useMemo(() => {
    if (!searchTerm.trim()) {
      return products;
    }
    const term = searchTerm.toLowerCase().trim();
    return products.filter(p => p.name.toLowerCase().includes(term));
  }, [products, searchTerm]);

  // Paginate results
  const totalPages = Math.ceil(filteredProducts.length / pageSize);
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredProducts.slice(start, start + pageSize);
  }, [filteredProducts, currentPage, pageSize]);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Check if product is in cart
  const getCartQuantity = (productId) => {
    const item = cartItems.find(i => i.productId === productId);
    return item ? item.quantity : 0;
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="product-search-container">
      {/* Search bar with + button */}
      <div className={styles['search-bar-wrapper']}>
        <div className={styles['search-input-container']}>
          <input
            type="text"
            placeholder="Buscar productos..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className={styles['clear-btn']} onClick={handleClearSearch}>
              &times;
            </button>
          )}
        </div>
        <button className={styles['add-product-btn']} onClick={onAddNewClick}>
          +
        </button>
      </div>

      {/* Product list */}
      {paginatedProducts.length > 0 ? (
        <>
          <div className={styles['product-list-container']}>
            {paginatedProducts.map(product => {
              const cartQty = getCartQuantity(product.id);
              return (
                <div
                  key={product.id}
                  className={`${styles['product-list-item']} ${cartQty > 0 ? styles['in-cart'] : ''}`}
                  onClick={() => onProductSelect(product)}
                >
                  <div className={styles['product-info']}>
                    <div className={styles['product-name']}>{product.name}</div>
                    <div className={styles['product-meta']}>
                      {product.getUnitDisplay()}
                      {product.getConversionDisplay() && ` | ${product.getConversionDisplay()}`}
                      {' | '}
                      {product.getPriceDisplay()}
                    </div>
                    {cartQty > 0 && (
                      <div className={styles['in-cart-badge']}>{cartQty} en orden</div>
                    )}
                  </div>
                  <div className={styles['tap-indicator']}>Toca para agregar</div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                &lt;
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    className={currentPage === pageNum ? styles.active : ''}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                &gt;
              </button>
            </div>
          )}
        </>
      ) : (
        <div className={styles['empty-state']}>
          <h3>No se encontraron productos</h3>
          <p>Intenta otra busqueda o</p>
          <p className={styles['add-suggestion']} onClick={onAddNewClick}>
            + Agregar un producto nuevo
          </p>
        </div>
      )}
    </div>
  );
};

export default ProductSearch;
